import * as React from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../chrome/AppShell';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import {
    Chipbar,
    MenuItem,
    MenuLabel,
    MenuSep,
    Segmented,
    TCard,
    TFoot,
    Toolbar,
    TRow,
} from '../components/table';
import { ConfirmDialog, Dialog, Field, Select, TextInput } from '../components/forms';
import { PageHeadActions, ViewSwitch } from '../components/nav';
import Timeline, { TLBooking, TLRow } from '../components/timeline';
import { Board, BoardCard } from '../components/board';
import { addDays, dayStart, endDayOf, mondayOf } from '../components/calendar';
import { ProdRow, useEventDetails, useProductionRange, useView } from '../lib/schedule';
import '../calendar.css';
import { Frow, ListState, useList, useToast } from '../components/roster';
import { humanize } from '../lib/jobs';

/*
 * Gear inventory, grouped by category. Assignments are edited on
 * activations, not here.
 */

const CATEGORIES = [
    'photobooth', 'printer', 'lighting', 'computer', 'phone',
    'tripods_stabilizers_rigs', 'audio', 'router', 'camera', 'lens', 'tablet',
];
const STUDIOS = [
    'nyc', 'nashville', 'atlanta', 'houston', 'austin', 'los_angeles',
    'seattle', 'spokane', 'chicago',
];
const STATUS: Record<string, { label: string; tone?: string }> = {
    available: { label: 'Available', tone: 'var(--good)' },
    in_use: { label: 'In use', tone: 'var(--text-secondary)' },
    needs_repair: { label: 'Needs repair', tone: 'var(--warn)' },
    retired: { label: 'Retired', tone: 'var(--text-tertiary)' },
};
const studio = (s?: string) =>
    s === 'nyc' ? 'NYC' : s === 'los_angeles' ? 'Los Angeles' : humanize(s);
const category = (c?: string) =>
    c === 'tripods_stabilizers_rigs' ? 'Tripods & rigs' : humanize(c);

type View = 'all' | 'available' | 'in_use' | 'needs_repair';
const VIEWS = ['list', 'schedule', 'board'] as const;
const DAY_MS = 86400000;
const fromDate = (d?: string | null) => (d ? dayStart(`${d}T12:00:00`) : null);

export default function GearV3() {
    const api = useAuthenticatedApi();
    const navigate = useNavigate();
    const [layout, setLayout] = useView<(typeof VIEWS)[number]>('gear', VIEWS, 'list');
    const toast = useToast();
    const gear = useList(() => api.get('/production/gear').then((r: any) => r.data));
    const [query, setQuery] = useState('');
    const [view, setView] = useState<View>('all');
    const [cat, setCat] = useState<string | null>(null);
    const [where, setWhere] = useState<string | null>(null);

    const rows = useMemo(() => gear.rows ?? [], [gear.rows]);
    const q = query.trim().toLowerCase();
    const visible = useMemo(
        () =>
            rows
                .filter((g: any) =>
                    !q ||
                    [g.name, g.brand, g.model, g.serialNumber].some((v) => (v ?? '').toLowerCase().includes(q)),
                )
                .filter((g: any) => view === 'all' || g.status === view)
                .filter((g: any) => !cat || g.category === cat)
                .filter((g: any) => !where || g.studioLocation === where),
        [rows, q, view, cat, where],
    );
    const groups = useMemo(() => {
        const by = new Map<string, any[]>();
        /* uncategorised gear groups under Other */
        visible.forEach((g: any) => by.set(g.category || 'other', [...(by.get(g.category || 'other') ?? []), g]));
        return CATEGORIES.filter((c) => by.has(c))
            .map((c) => ({ c, rows: by.get(c)! }))
            .concat(Array.from(by.keys()).filter((c) => !CATEGORIES.includes(c)).map((c) => ({ c, rows: by.get(c)! })));
    }, [visible]);
    const available = rows.filter((g: any) => g.status === 'available').length;
    const repair = rows.filter((g: any) => g.status === 'needs_repair').length;

    /* Bookings come from each event's gear assignments (checkout/return show as shipping).
       Board columns are derived from those dates plus the stored status. */
    const today = dayStart(Date.now());
    const [tlStart, setTlStart] = useState(() => mondayOf(Date.now()));
    /* a month back for long events; a few days ahead for kits that ship early */
    const rangeFrom = layout === 'board' ? addDays(today, -31) : addDays(tlStart, -31);
    const rangeTo = layout === 'board' ? addDays(today, 10) : addDays(tlStart, 16);
    const range = useProductionRange(rangeFrom, rangeTo, layout !== 'list');
    const events = (range.rows ?? []).filter((r) => !r.isProvisional && r.startAt);
    const details = useEventDetails(events.map((r) => r.activationId), ['prod']);
    const kit = useMemo(
        () =>
            events.flatMap((r: ProdRow) =>
                ((details[r.activationId]?.prod?.gearAssignments ?? []) as any[]).map((a) => {
                    const startDay = dayStart(r.startAt!);
                    const endDay = endDayOf(r.endAt ?? r.startAt!, startDay);
                    const out = fromDate(a.checkoutDate) ?? startDay;
                    const back = fromDate(a.returnDate) ?? endDay;
                    return {
                        id: `${r.activationId}-${a.gearAssetId}`,
                        rowId: a.gearAssetId as number,
                        name: r.name,
                        startDay,
                        endDay,
                        out,
                        back,
                        kind: 'confirmed' as const,
                        lead: Math.max(0, Math.round((startDay - out) / DAY_MS)),
                        tail: Math.max(0, Math.round((back - endDay) / DAY_MS)),
                        href: `/activations/${r.activationId}`,
                    };
                }),
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [range.rows, details],
    );
    const tlRows: TLRow[] = groups.flatMap((grp) =>
        grp.rows
            .filter((g: any) => g.status !== 'retired' || view !== 'all')
            .map((g: any) => ({
                id: g.id,
                name: g.name,
                sub: [studio(g.studioLocation), g.status === 'needs_repair' ? 'Needs repair' : ''].filter(Boolean).join(' · '),
                group: category(grp.c),
                onOpen: () => setOpened(g),
            })),
    );
    const tlBookings: TLBooking[] = kit;
    const fmtDay = (t: number) =>
        t === today
            ? 'today'
            : t === addDays(today, 1)
              ? 'tomorrow'
              : Math.round((t - today) / DAY_MS) < 7
            ? new Date(t).toLocaleDateString(undefined, { weekday: 'short' })
            : new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const boardCols = useMemo(() => {
        const cols: Record<string, Array<{ g: any; foot: React.ReactNode }>> = { available: [], soon: [], out: [], repair: [] };
        visible.forEach((g: any) => {
            if (g.status === 'retired') return;
            const mine = kit.filter((k) => k.rowId === g.id);
            const now =
                mine.find((k) => k.startDay <= today && k.endDay >= today) ??
                mine.find((k) => k.out <= today && k.back >= today);
            const next = mine.filter((k) => k.out > today && k.out <= addDays(today, 3)).sort((a, b) => a.out - b.out)[0];
            if (g.status === 'needs_repair') cols.repair.push({ g, foot: next ? <span className="warn">Booked {fmtDay(next.out)} · {next.name}</span> : null });
            else if (now || g.status === 'in_use')
                cols.out.push({ g, foot: now ? <span>{now.name} · back {fmtDay(now.back)}</span> : <span>Marked in use</span> });
            else if (next) cols.soon.push({ g, foot: <span>Out {fmtDay(next.out)} · {next.name}</span> });
            else cols.available.push({ g, foot: <span>{studio(g.studioLocation)}</span> });
        });
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, kit, today]);

    const setStatus = async (g: any, status: string) => {
        /* the current row, not the snapshot a dialog was opened with */
        const before = (gear.rows ?? []).find((x: any) => x.id === g.id)?.status ?? g.status;
        const apply = (s: string) =>
            gear.setRows((list) => (list ?? []).map((x: any) => (x.id === g.id ? { ...x, status: s } : x)));
        apply(status);
        const saving = api.put(`/production/gear/${g.id}`, { status });
        toast.show(`${g.name} · ${STATUS[status].label}`, async () => {
            apply(before);
            try {
                await saving.catch(() => {});
                await api.put(`/production/gear/${g.id}`, { status: before });
            } catch {
                gear.reload();
                toast.show(`Couldn’t undo ${g.name}.`);
            }
        });
        try {
            await saving;
        } catch {
            apply(before);
            toast.show(`Couldn’t update ${g.name}.`);
        }
    };
    const [removing, setRemoving] = useState<any>(null);
    const [opened, setOpened] = useState<any>(null);
    const remove = async (g: any) => {
        gear.setRows((list) => (list ?? []).filter((x: any) => x.id !== g.id));
        try {
            await api.delete(`/production/gear/${g.id}`);
            toast.show(`Removed ${g.name}.`);
        } catch {
            gear.reload();
            toast.show(`Couldn’t remove ${g.name}.`);
        }
    };

    const blank = { name: '', category: 'photobooth', studioLocation: 'chicago', brand: '', model: '', serialNumber: '' };
    const [adding, setAdding] = useState(false);
    const [f, setF] = useState(blank);
    const [saving, setSaving] = useState(false);
    const [addError, setAddError] = useState('');
    const add = async () => {
        if (!f.name.trim() || saving) return;
        setSaving(true);
        try {
            await api.post('/production/gear', {
                name: f.name.trim(),
                category: f.category,
                studioLocation: f.studioLocation,
                brand: f.brand.trim() || null,
                model: f.model.trim() || null,
                serialNumber: f.serialNumber.trim() || null,
            });
            setAdding(false);
            setF(blank);
            toast.show(`Added ${f.name.trim()}.`);
            gear.reload();
        } catch {
            setAddError('Couldn’t add this asset. Try again.');
        } finally {
            setSaving(false);
        }
    };

    const row = (g: any, i: number) => {
        const st = STATUS[g.status] ?? { label: humanize(g.status) };
        return (
            <TRow
                key={g.id}
                index={i}
                name={g.name}
                meta={[[g.brand, g.model].filter(Boolean).join(' '), g.serialNumber].filter(Boolean).join(' · ')}
                metaDate={studio(g.studioLocation)}
                date={studio(g.studioLocation)}
                status={<span style={{ color: st.tone }}>{st.label}</span>}
                onOpen={() => setOpened(g)}
                menuItems={[
                    ...Object.keys(STATUS)
                        .filter((s) => s !== g.status)
                        .map((s) => ({ label: `Mark ${STATUS[s].label.toLowerCase()}`, onClick: () => setStatus(g, s) })),
                    { label: 'Remove…', onClick: () => setRemoving(g) },
                ]}
            />
        );
    };

    const filtered = !!q || view !== 'all' || !!cat || !!where;
    return (
        <AppShell>
            <PageHeadActions
                title="Gear"
                sub={gear.rows ? `${rows.length} assets · ${available} available${repair ? ` · ${repair} need repair` : ''}` : 'Equipment inventory'}
                actions={
                    <>
                        <ViewSwitch
                            value={layout}
                            onChange={setLayout}
                            options={[
                                { value: 'list', label: 'List' },
                                { value: 'schedule', label: 'Schedule' },
                                { value: 'board', label: 'Board' },
                            ]}
                        />
                        <button className="tb-new" onClick={() => { setAddError(''); setAdding(true); }}>
                            Add gear
                        </button>
                    </>
                }
            />
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search name, model, or serial"
                filterCount={(cat ? 1 : 0) + (where ? 1 : 0)}
                inline={
                    layout !== 'board' && <Segmented
                        label="Status"
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'available', label: 'Available' },
                            { value: 'in_use', label: 'In use' },
                            { value: 'needs_repair', label: 'Repair' },
                        ]}
                        value={view}
                        onChange={setView}
                    />
                }
                menu={(close) => (
                    <>
                        <MenuLabel>Category</MenuLabel>
                        {Array.from(new Set(rows.map((g: any) => g.category).filter(Boolean))).map((c: any) => (
                            <MenuItem key={c} on={cat === c} onClick={() => { setCat(cat === c ? null : c); close(); }}>
                                {category(c)}
                            </MenuItem>
                        ))}
                        <MenuSep />
                        <MenuLabel>Studio</MenuLabel>
                        {Array.from(new Set(rows.map((g: any) => g.studioLocation).filter(Boolean))).map((s: any) => (
                            <MenuItem key={s} on={where === s} onClick={() => { setWhere(where === s ? null : s); close(); }}>
                                {studio(s)}
                            </MenuItem>
                        ))}
                    </>
                )}
            />
            <Chipbar
                chips={[
                    ...(cat ? [{ label: 'Category', value: category(cat), onClear: () => setCat(null) }] : []),
                    ...(where ? [{ label: 'Studio', value: studio(where), onClear: () => setWhere(null) }] : []),
                ]}
            />
            <ListState
                rows={gear.rows}
                failed={gear.failed}
                what="gear"
                reload={gear.reload}
                empty={
                    visible.length === 0
                        ? rows.length === 0
                            ? 'No gear yet.'
                            : q
                              ? `Nothing matches “${query}”.`
                              : 'Nothing matches these filters.'
                        : undefined
                }
                emptyAction={rows.length === 0 ? 'Add gear' : undefined}
                onEmptyAction={() => setAdding(true)}
            >
                {layout === 'list' &&
                    groups.map((grp, gi) => (
                        <React.Fragment key={grp.c}>
                            <p className="s-lab pl-group" style={gi === 0 ? { marginTop: 4 } : undefined}>
                                {category(grp.c)} · {grp.rows.length}
                            </p>
                            <TCard>{grp.rows.map(row)}</TCard>
                        </React.Fragment>
                    ))}
                {layout === 'schedule' && (
                    <Timeline
                        title="Gear schedule"
                        what="items"
                        rows={tlRows}
                        bookings={tlBookings}
                        start={tlStart}
                        onShift={(n) => setTlStart((t) => addDays(t, 7 * n))}
                        onToday={() => setTlStart(mondayOf(Date.now()))}
                        navigate={navigate}
                        loading={range.rows === null}
                    />
                )}
                {layout === 'board' && (
                    <Board
                        label="Gear by status"
                        columns={[
                            { key: 'available', label: 'Available', tone: 'var(--good)', list: boardCols.available },
                            { key: 'soon', label: 'Going out soon', list: boardCols.soon },
                            { key: 'out', label: 'Out now', tone: 'var(--text-secondary)', list: boardCols.out },
                            { key: 'repair', label: 'Needs repair', tone: 'var(--warn)', list: boardCols.repair },
                        ].map((c) => ({
                            key: c.key,
                            label: c.label,
                            tone: c.tone,
                            count: c.list.length,
                            children: c.list.map(({ g, foot }) => (
                                <BoardCard
                                    key={g.id}
                                    name={g.name}
                                    meta={[category(g.category), [g.brand, g.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                                    foot={foot}
                                    onOpen={() => setOpened(g)}
                                />
                            )),
                        }))}
                    />
                )}
            </ListState>
            {gear.rows && (
                <TFoot>
                    {filtered ? `${visible.length} of ${rows.length} · filtered` : `${rows.length} assets`}
                    {layout === 'board' && rows.some((g: any) => g.status === 'retired') ? ' · retired gear not shown' : ''}
                    {layout !== 'list' ? ' · view only' : ''}
                </TFoot>
            )}

            <Dialog
                open={adding}
                title="Add gear"
                onClose={() => setAdding(false)}
                onSubmit={add}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setAdding(false)}>Cancel</button>
                        <button type="submit" className="tb-new" disabled={!f.name.trim() || saving}>{saving ? 'Adding…' : 'Add gear'}</button>
                    </>
                }
            >
                <Field label="Name" help="What the crew calls it — “Mirror Booth #3”." error={addError || undefined}>
                    <TextInput autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                </Field>
                <div className="two">
                    <Field label="Category">
                        <Select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
                            {CATEGORIES.map((c) => <option key={c} value={c}>{category(c)}</option>)}
                        </Select>
                    </Field>
                    <Field label="Studio">
                        <Select value={f.studioLocation} onChange={(e) => setF({ ...f, studioLocation: e.target.value })}>
                            {STUDIOS.map((s) => <option key={s} value={s}>{studio(s)}</option>)}
                        </Select>
                    </Field>
                </div>
                <div className="two">
                    <Field label="Brand (optional)">
                        <TextInput value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} />
                    </Field>
                    <Field label="Model (optional)">
                        <TextInput value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
                    </Field>
                </div>
                <Field label="Serial number (optional)">
                    <TextInput value={f.serialNumber} onChange={(e) => setF({ ...f, serialNumber: e.target.value })} />
                </Field>
            </Dialog>
            <Dialog
                open={opened !== null}
                title={opened?.name ?? ''}
                onClose={() => setOpened(null)}
                footer={
                    <>
                        <button type="button" className="tb-btn danger-soft" onClick={() => { setRemoving(opened); setOpened(null); }}>Remove…</button>
                        <button type="button" className="tb-btn" onClick={() => setOpened(null)}>Close</button>
                    </>
                }
            >
                {opened && (
                    <>
                        <Frow label="Category">{category(opened.category)}</Frow>
                        <Frow label="Studio">{studio(opened.studioLocation)}</Frow>
                        <Frow label="Brand · model">{[opened.brand, opened.model].filter(Boolean).join(' ') || '—'}</Frow>
                        <Frow label="Serial">{opened.serialNumber || '—'}</Frow>
                        <p className="s-lab" style={{ margin: '14px 0 8px' }}>Status</p>
                        <div className="statusgrid" role="group" aria-label="Status">
                            {Object.keys(STATUS).map((k) => {
                                const cur = (gear.rows ?? []).find((x: any) => x.id === opened.id)?.status;
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        aria-pressed={cur === k}
                                        className={cur === k ? 'on' : ''}
                                        onClick={() => cur !== k && setStatus(opened, k)}
                                    >
                                        <span className="sw" style={{ background: STATUS[k].tone }} />
                                        {STATUS[k].label}
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </Dialog>
            <ConfirmDialog
                open={removing !== null}
                title={`Remove “${removing?.name ?? ''}”?`}
                body="It leaves the inventory and any upcoming event assignments. This can’t be undone."
                actionLabel="Remove"
                onConfirm={() => removing && remove(removing)}
                onClose={() => setRemoving(null)}
            />
            {toast.node}
        </AppShell>
    );
}
